import { useState } from 'react';
import cn from 'classnames';

import Select from './Select';
import { IconCircleArrow } from './Icons';

import { shortMonths } from '../utils/time';

const MIN_VIEW_YEAR = 2018;
const MAX_VIEW_YEAR = new Date().getFullYear();
const MAX_VIEW_MONTH = new Date().getMonth() + 1;

interface MonthYearSelectorProps {
  onChange: (monthYear: [number, number]) => void;
}

export default function MonthYearSelector({ onChange }: MonthYearSelectorProps) {
  // month values are 0 (no month selected), 1 (Jan), ..., 12 (Dec)
  const [[month, year], setTime] = useState([new Date().getMonth() + 1, new Date().getFullYear()]);

  const changeTime = (time: [number, number]) => {
    setTime(time);
    onChange(time);
  };

  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const label =
    month > 0
      ? `${new Date(year, month - 1).toLocaleString('default', { month: 'long' })} ${year}`
      : `${year}`;

  return (
    <Select label={label}>
      <div className="p-3 pt-1">
        <div className="flex items-center justify-between">
          <button
            type="button"
            className={cn('h-full py-3 pr-1 mr-1 rounded', {
              'text-gray-400': viewYear <= MIN_VIEW_YEAR,
            })}
            onClick={() => setViewYear(viewYear - 1)}
            disabled={viewYear <= MIN_VIEW_YEAR}
          >
            <IconCircleArrow className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="w-full text-lg font-bold rounded hover:bg-blue-500 hover:text-white"
            onClick={() => changeTime([0, viewYear])}
            disabled={month === 0 && viewYear === year}
          >
            {viewYear}
          </button>
          <button
            type="button"
            className={cn('h-full py-3 pl-1 ml-1 rounded', {
              'text-gray-400': viewYear >= MAX_VIEW_YEAR,
            })}
            onClick={() => setViewYear(viewYear + 1)}
            disabled={viewYear >= MAX_VIEW_YEAR}
          >
            <IconCircleArrow className="w-5 h-5 transform rotate-180" />
          </button>
        </div>
        <div className="w-full text-center border-t border-gray-400 pt-1.5">
          {shortMonths.map((name, index) => {
            const selected = index + 1 === month && viewYear === year;
            const future = viewYear >= MAX_VIEW_YEAR && index + 1 > MAX_VIEW_MONTH;

            return (
              <button
                type="button"
                className={cn('w-1/4 py-1 my-0.5 rounded border-2 border-transparent', {
                  'font-bold text-blue-500 hover:border-blue-500': selected,
                  'hover:bg-blue-500 hover:text-white': !selected && !future,
                  'text-gray-400': future,
                })}
                disabled={selected || future}
                onClick={() => changeTime([index + 1, viewYear])}
                key={name}
              >
                {name}
              </button>
            );
          })}
        </div>
      </div>
    </Select>
  );
}
